import { useAppKitAccount, useAppKitNetwork, useAppKitProvider } from '@reown/appkit/react'
import { useWeb3React } from '@web3-react/core'
import { Provider } from '@web3-react/types'
import { reownConnection } from 'connection'
import { ConnectionType } from 'connection/types'
import { useEffect, useRef } from 'react'
import { reownEip1193Provider } from 'reown/eip1193Provider'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { updateSelectedWallet } from 'state/user/reducer'

export default function ReownWeb3Bridge() {
  const dispatch = useAppDispatch()
  const selectedWallet = useAppSelector((state) => state.user.selectedWallet)
  // Keep a ref so the effect can read the latest value without it being a dep
  const selectedWalletRef = useRef(selectedWallet)
  selectedWalletRef.current = selectedWallet

  const { address, isConnected } = useAppKitAccount({ namespace: 'eip155' })
  const { chainId } = useAppKitNetwork()
  const { walletProvider } = useAppKitProvider<Provider>('eip155')

  // web3-react account — used for recovery detection only
  const { account: web3Account } = useWeb3React()

  // Always keep the low-level provider in sync immediately (no gate needed)
  useEffect(() => {
    reownEip1193Provider.setProvider(walletProvider)
  }, [walletProvider])

  // Primary sync: Reown session → web3-react
  useEffect(() => {
    // Only disconnect when Reown definitively says the session is gone.
    // Do NOT disconnect just because walletProvider is transiently undefined
    // (happens during WalletConnect session refresh / chain switch).
    if (!isConnected || !address) {
      reownConnection.connector.resetState()
      if (selectedWalletRef.current === ConnectionType.REOWN) {
        dispatch(updateSelectedWallet({ wallet: undefined }))
      }
      return
    }

    // Wait until the provider is actually available before activating.
    // This avoids an activate() call with an empty provider during initial handshake.
    if (!walletProvider) return

    let stale = false
    Promise.resolve(reownConnection.connector.activate())
      .then(() => {
        if (!stale) {
          dispatch(updateSelectedWallet({ wallet: ConnectionType.REOWN }))
        }
      })
      .catch((error: unknown) => {
        console.debug(`Reown web3-react bridge error: ${error}`)
      })

    return () => {
      stale = true
    }
    // selectedWallet is intentionally excluded: it is read via ref to break
    // the feedback loop where dispatching updateSelectedWallet would re-trigger
    // this effect and potentially see a transiently-undefined walletProvider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, chainId, dispatch, isConnected, walletProvider])

  // Recovery effect: if Reown session is alive but web3-react lost its state
  // (e.g. MetaMask emitted a network-level `disconnect` event which previously
  // caused the EIP1193 connector to call resetState()), re-activate silently.
  useEffect(() => {
    if (!isConnected || !address || !walletProvider) return
    if (web3Account) return // already in sync, nothing to do

    let stale = false
    Promise.resolve(reownConnection.connector.activate())
      .then(() => {
        if (!stale) {
          dispatch(updateSelectedWallet({ wallet: ConnectionType.REOWN }))
        }
      })
      .catch((error: unknown) => {
        console.debug(`Reown recovery re-activation error: ${error}`)
      })

    return () => {
      stale = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [web3Account, isConnected, address, walletProvider, dispatch])

  return null
}
